import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import moment from "moment";
import { HiPlus, HiOutlineTrash, HiSave, HiLockClosed } from "react-icons/hi";
import { RiRobot2Fill } from "react-icons/ri";
import { CustomButton } from "../../../atoms/CustomButton";
import { Switch } from "../../../atoms/Switch";
import { Textarea, Select, TextInput } from "flowbite-react";
import { Spinner } from "../../../atoms/Spinner";
import { FieldLabel } from "../../../atoms/FieldLabel";
import {
  AutoPilotConfiguration,
  AutoPilotSendWindow,
  PromotionIntervalUnit,
} from "../../../../constants/types";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import { PlanTier, usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { UpgradePromptContent } from "../../../atoms/UpgradePromptContent";
import { PlansModal } from "../../../modals/PlansModal";
import useAuth from "../../../../hooks/useAuth";
import {
  filled,
  AUTO_PILOT_LANGUAGES,
  AUTO_PILOT_LIMITS,
  DEFAULT_SEND_WINDOW,
  WEEK_DAYS,
  autoPilotLanguage,
  autoPilotProblem,
  autoPilotToSave,
  sendWindowProblem,
  withAutoPilotDefaults,
} from "./autoPilotConfig";

const TIME_FIELD =
  "block w-full min-w-0 rounded-md border border-gray-300 bg-white p-2.5 text-base text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 md:py-2 md:text-sm";

interface AutoPilotSettingsProps {
  data?: {
    auto_pilot?: AutoPilotConfiguration;
  };
  /** The language the server takes when none is chosen, from the owner's country; unknown while loading. */
  ownerLanguage?: string;
  /** Resolves with the settings the server kept, when it says. */
  onSubmit: (
    autoPilotConfig: AutoPilotConfiguration
  ) => Promise<AutoPilotConfiguration | undefined | void>;
}

const AutoPilotSettings: React.FC<AutoPilotSettingsProps> = ({
  data,
  ownerLanguage,
  onSubmit,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const [showPlansModal, setShowPlansModal] = useState(false);

  const hasAutoPilotAccess = permissions.hasFeatureAccess("AUTO_PILOT");
  const canWrite = permissions.can(Cap.LOYALTY, Level.WRITE);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [autoPilotConfig, setAutoPilotConfig] =
    useState<AutoPilotConfiguration>(() =>
      withAutoPilotDefaults(data?.auto_pilot)
    );

  useEffect(() => {
    if (data?.auto_pilot) {
      setAutoPilotConfig(withAutoPilotDefaults(data.auto_pilot));
    }
  }, [data?.auto_pilot]);

  const language = autoPilotLanguage(autoPilotConfig.language, ownerLanguage);

  const languageOptions = useMemo(() => {
    let names: Intl.DisplayNames | null = null;
    try {
      names = new Intl.DisplayNames([i18n.language], { type: "language" });
    } catch {
      names = null;
    }
    return AUTO_PILOT_LANGUAGES.map((code) => {
      const name = names?.of(code) || code;
      return {
        code,
        name: name.charAt(0).toLocaleUpperCase(i18n.language) + name.slice(1),
      };
    }).sort((a, b) => a.name.localeCompare(b.name, i18n.language));
  }, [i18n.language]);

  const sendWindow = autoPilotConfig.send_window ?? DEFAULT_SEND_WINDOW;
  const windowProblem = sendWindowProblem(sendWindow);

  const handleToggleEnabled = useCallback(
    async (enabled: boolean) => {
      const newConfig = { ...autoPilotConfig, enabled };
      setAutoPilotConfig(newConfig);

      if (!enabled) {
        try {
          setIsSubmitting(true);
          await onSubmit(newConfig);
          toast.success(t("dashboard.features.lmc.autoPilot.disabled"));
        } catch (error) {
          setAutoPilotConfig((prev) => ({ ...prev, enabled: !enabled }));
        } finally {
          setIsSubmitting(false);
        }
      }
    },
    [autoPilotConfig, onSubmit, t]
  );

  const handleToggleAllowPromotion = useCallback((allowed: boolean) => {
    setAutoPilotConfig((prev) => ({
      ...prev,
      allow_promotion: allowed,
      promotion_messages: allowed
        ? prev.promotion_messages.length > 0
          ? prev.promotion_messages
          : [""]
        : [],
    }));
  }, []);

  const handleIntervalValueChange = (value: string) => {
    setAutoPilotConfig((prev) => ({
      ...prev,
      promotion_interval_value: value === "" ? undefined : Number(value),
    }));
  };

  const handleIntervalUnitChange = (unit: PromotionIntervalUnit) => {
    setAutoPilotConfig((prev) => ({ ...prev, promotion_interval_unit: unit }));
  };

  const handleDescriptionChange = (value: string) => {
    setAutoPilotConfig((prev) => ({ ...prev, description: value }));
  };

  const updateSendWindow = (change: Partial<AutoPilotSendWindow>) => {
    setAutoPilotConfig((prev) => ({
      ...prev,
      send_window: {
        ...(prev.send_window ?? DEFAULT_SEND_WINDOW),
        ...change,
      },
    }));
  };

  const toggleSendDay = (day: number, checked: boolean) => {
    updateSendWindow({
      days: checked
        ? WEEK_DAYS.filter((d) => d === day || sendWindow.days.includes(d))
        : sendWindow.days.filter((d) => d !== day),
    });
  };

  const handleAddPromotionalMessage = () => {
    if (
      autoPilotConfig.promotional_messages.length >=
      AUTO_PILOT_LIMITS.toneExamplesMax
    ) {
      toast.warning(
        t("dashboard.features.lmc.autoPilot.maxPromotionalMessages")
      );
      return;
    }
    setAutoPilotConfig((prev) => ({
      ...prev,
      promotional_messages: [...prev.promotional_messages, ""],
    }));
  };

  const handleRemovePromotionalMessage = (index: number) => {
    setAutoPilotConfig((prev) => ({
      ...prev,
      promotional_messages: prev.promotional_messages.filter(
        (_, i) => i !== index
      ),
    }));
  };

  const handlePromotionalMessageChange = (index: number, value: string) => {
    setAutoPilotConfig((prev) => ({
      ...prev,
      promotional_messages: prev.promotional_messages.map((msg, i) =>
        i === index ? value : msg
      ),
    }));
  };

  const handleAddPromotionMessage = () => {
    if (
      autoPilotConfig.promotion_messages.length >= AUTO_PILOT_LIMITS.offersMax
    ) {
      toast.warning(t("dashboard.features.lmc.autoPilot.maxPromotionMessages"));
      return;
    }
    setAutoPilotConfig((prev) => ({
      ...prev,
      promotion_messages: [...prev.promotion_messages, ""],
    }));
  };

  const handleRemovePromotionMessage = (index: number) => {
    setAutoPilotConfig((prev) => ({
      ...prev,
      promotion_messages: prev.promotion_messages.filter((_, i) => i !== index),
    }));
  };

  const handlePromotionMessageChange = (index: number, value: string) => {
    setAutoPilotConfig((prev) => ({
      ...prev,
      promotion_messages: prev.promotion_messages.map((msg, i) =>
        i === index ? value : msg
      ),
    }));
  };

  const handleSaveConfiguration = async () => {
    if (!canWrite) return;
    const problem = autoPilotProblem(autoPilotConfig);
    if (problem) {
      toast.error(t(`dashboard.features.lmc.autoPilot.${problem}`));
      return;
    }

    try {
      setIsSubmitting(true);
      const kept = await onSubmit(
        autoPilotToSave(autoPilotConfig, ownerLanguage)
      );
      if (kept && kept.enabled !== true) {
        setAutoPilotConfig(withAutoPilotDefaults(kept));
        toast.warning(t("dashboard.features.lmc.autoPilot.notTurnedOn"));
      } else {
        toast.success(t("dashboard.features.lmc.autoPilot.saved"));
      }
    } catch (error) {
      toast.error(t("dashboard.features.lmc.autoPilot.saveError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasAutoPilotAccess) {
    return (
      <div className="p-4 border border-gray-200 rounded-lg bg-white space-y-6">
        <div className="flex items-center gap-2">
          <RiRobot2Fill className="text-gray-400 flex-shrink-0" size={18} />
          <h3 className="text-base font-semibold text-gray-900">
            {t("dashboard.features.lmc.autoPilot.catchyTitle")}
          </h3>
        </div>

        {data?.auto_pilot && (
          <p className="text-sm text-gray-600">
            {t("dashboard.features.lmc.autoPilot.offWithoutAccess")}
          </p>
        )}

        <UpgradePromptContent
          icon={HiLockClosed}
          iconTheme="blue"
          title={t("dashboard.features.lmc.autoPilot.upgrade.title")}
          description={t(
            "dashboard.features.lmc.autoPilot.upgrade.description"
          )}
          benefits={[
            {
              id: "auto-engagement",
              text: t("dashboard.features.lmc.autoPilot.upgrade.benefit1"),
            },
            {
              id: "personalized-messages",
              text: t("dashboard.features.lmc.autoPilot.upgrade.benefit2"),
            },
            {
              id: "promotional-automation",
              text: t("dashboard.features.lmc.autoPilot.upgrade.benefit3"),
            },
            {
              id: "time-saving",
              text: t("dashboard.features.lmc.autoPilot.upgrade.benefit4"),
            },
            {
              id: "win-back",
              text: t("dashboard.features.lmc.autoPilot.upgrade.benefit5"),
            },
          ]}
          upgradeText={t("dashboard.features.lmc.autoPilot.upgrade.button")}
          onUpgrade={() => setShowPlansModal(true)}
          className="w-full"
        />

        <PlansModal
          showPlansModal={showPlansModal}
          setShowPlansModal={setShowPlansModal}
          targetPlan={PlanTier.ENTERPRISE}
        />
      </div>
    );
  }

  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-white space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <RiRobot2Fill className="text-gray-400 flex-shrink-0" size={18} />
          <h3 className="text-base font-semibold text-gray-900">
            {t("dashboard.features.lmc.autoPilot.catchyTitle")}
          </h3>
        </div>
        <Switch
          checked={autoPilotConfig.enabled}
          disabled={isSubmitting || !canWrite}
          onChange={handleToggleEnabled}
        />
      </div>

      <p className="text-gray-600 text-sm">
        {t("dashboard.features.lmc.autoPilot.subtitle")}
      </p>

      {autoPilotConfig.enabled && (
        <fieldset
          disabled={!canWrite}
          className="space-y-6 border-0 m-0 p-0 min-w-0"
        >
          <div>
            <FieldLabel htmlFor="auto-pilot-description">
              {t("dashboard.features.lmc.autoPilot.descriptionPlaceTitle")}
            </FieldLabel>
            <p className="mb-1 text-xs text-gray-500">
              {t("dashboard.features.lmc.autoPilot.descriptionHelp")}
            </p>
            <Textarea
              id="auto-pilot-description"
              value={autoPilotConfig.description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              placeholder={
                t(
                  "dashboard.features.lmc.autoPilot.descriptionPlaceholder"
                ) as string
              }
              rows={6}
              className="w-full"
            />
            <div className="flex justify-between items-center mt-0">
              <span
                className={`text-xs whitespace-nowrap ${
                  autoPilotConfig.description.length <
                  AUTO_PILOT_LIMITS.descriptionMin
                    ? "text-amber-600"
                    : "text-gray-500"
                }`}
              >
                {t("dashboard.features.lmc.autoPilot.minChars", {
                  min: AUTO_PILOT_LIMITS.descriptionMin,
                })}
              </span>
              <CharacterCounter
                currentLength={autoPilotConfig.description.length}
                maxLength={AUTO_PILOT_LIMITS.descriptionMax}
                className="mt-0"
              />
            </div>
          </div>

          {language && (
            <div>
              <FieldLabel htmlFor="auto-pilot-language">
                {t("dashboard.features.lmc.autoPilot.languageTitle")}
              </FieldLabel>
              <p className="mb-1 text-xs text-gray-500">
                {t("dashboard.features.lmc.autoPilot.languageHelp")}
              </p>
              <Select
                id="auto-pilot-language"
                value={language}
                onChange={(e) =>
                  setAutoPilotConfig((prev) => ({
                    ...prev,
                    language: e.target.value,
                  }))
                }
                className="w-full sm:w-64"
              >
                {languageOptions.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.name}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-3">
              <div>
                <FieldLabel htmlFor="promotional-messages" className="mb-0">
                  {t(
                    "dashboard.features.lmc.autoPilot.promotionalMessagesTitle"
                  )}
                </FieldLabel>
                <p className="mb-1 text-xs text-gray-500">
                  {t(
                    "dashboard.features.lmc.autoPilot.promotionalMessagesHelp"
                  )}
                </p>
              </div>
              {autoPilotConfig.promotional_messages.length <
                AUTO_PILOT_LIMITS.toneExamplesMax && (
                <CustomButton
                  type="button"
                  id="promotional-messages"
                  variant="none"
                  size="sm"
                  onClick={handleAddPromotionalMessage}
                  className="shrink-0 self-start gap-1 whitespace-nowrap px-0 text-blue-600 enabled:hover:text-blue-700 sm:mt-6"
                  icon={<HiPlus size={16} />}
                  title={t("dashboard.features.lmc.autoPilot.addMessage")}
                />
              )}
            </div>
            <div className="space-y-3">
              {autoPilotConfig.promotional_messages.map((message, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="flex-1">
                    <TextInput
                      value={message}
                      onChange={(e) =>
                        handlePromotionalMessageChange(index, e.target.value)
                      }
                      placeholder={
                        t(
                          "dashboard.features.lmc.autoPilot.messagePlaceholder"
                        ) as string
                      }
                      maxLength={AUTO_PILOT_LIMITS.toneExampleMaxLength}
                      className="w-full"
                    />
                    <CharacterCounter
                      currentLength={message.length}
                      maxLength={AUTO_PILOT_LIMITS.toneExampleMaxLength}
                    />
                  </div>
                  {autoPilotConfig.promotional_messages.length > 1 && (
                    <CustomButton
                      type="button"
                      variant="none"
                      size="icon"
                      onClick={() => handleRemovePromotionalMessage(index)}
                      className="mt-1 rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                      icon={<HiOutlineTrash size={18} />}
                      aria-label={t("common.delete") as string}
                    />
                  )}
                </div>
              ))}
            </div>
            <p
              className={`text-xs mt-2 ${
                filled(autoPilotConfig.promotional_messages).length <
                AUTO_PILOT_LIMITS.toneExamplesMin
                  ? "text-red-500"
                  : "text-gray-500"
              }`}
            >
              {t("dashboard.features.lmc.autoPilot.messagesCount", {
                count: filled(autoPilotConfig.promotional_messages).length,
                min: AUTO_PILOT_LIMITS.toneExamplesMin,
                max: AUTO_PILOT_LIMITS.toneExamplesMax,
              })}
            </p>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <FieldLabel htmlFor="auto-pilot-cap">
              {t("dashboard.features.lmc.autoPilot.promotionIntervalTitle")}
            </FieldLabel>
            <p className="mb-1 text-xs text-gray-500">
              {t("dashboard.features.lmc.autoPilot.promotionIntervalHelp")}
            </p>
            <div className="flex items-center gap-3">
              <p className="pr-1 text-sm">
                {t("dashboard.features.lmc.autoPilot.max")}
              </p>
              <TextInput
                id="auto-pilot-cap"
                type="number"
                min={AUTO_PILOT_LIMITS.capMin}
                max={AUTO_PILOT_LIMITS.capMax}
                step={1}
                value={autoPilotConfig.promotion_interval_value ?? ""}
                onChange={(e) => handleIntervalValueChange(e.target.value)}
                className="w-20"
              />
              <p className="px-1 text-sm">
                {t("dashboard.features.lmc.autoPilot.per")}
              </p>
              <Select
                value={autoPilotConfig.promotion_interval_unit || "day"}
                onChange={(e) =>
                  handleIntervalUnitChange(
                    e.target.value as PromotionIntervalUnit
                  )
                }
                className="w-32"
              >
                <option value="hour">
                  {t("dashboard.features.lmc.autoPilot.intervalUnit.hour")}
                </option>
                <option value="day">
                  {t("dashboard.features.lmc.autoPilot.intervalUnit.day")}
                </option>
                <option value="week">
                  {t("dashboard.features.lmc.autoPilot.intervalUnit.week")}
                </option>
                <option value="month">
                  {t("dashboard.features.lmc.autoPilot.intervalUnit.month")}
                </option>
                <option value="year">
                  {t("dashboard.features.lmc.autoPilot.intervalUnit.year")}
                </option>
              </Select>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <FieldLabel htmlFor="auto-pilot-send-start">
              {t("dashboard.features.lmc.autoPilot.sendWindowTitle")}
            </FieldLabel>
            <p className="mb-2 text-xs text-gray-500">
              {t("dashboard.features.lmc.autoPilot.sendWindowHelp")}
            </p>
            <div className="flex max-w-sm items-center gap-2">
              <span className="shrink-0 text-sm text-gray-700">
                {t("dashboard.features.lmc.autoPilot.sendWindowFrom")}
              </span>
              <input
                id="auto-pilot-send-start"
                type="time"
                step={900}
                value={sendWindow.start}
                onChange={(e) => updateSendWindow({ start: e.target.value })}
                aria-label={
                  t(
                    "dashboard.features.lmc.autoPilot.sendWindowStart"
                  ) as string
                }
                className={TIME_FIELD}
              />
              <span className="shrink-0 text-sm text-gray-700">
                {t("dashboard.features.lmc.autoPilot.sendWindowTo")}
              </span>
              <input
                type="time"
                step={900}
                value={sendWindow.end}
                onChange={(e) => updateSendWindow({ end: e.target.value })}
                aria-label={
                  t("dashboard.features.lmc.autoPilot.sendWindowEnd") as string
                }
                className={TIME_FIELD}
              />
            </div>
            <fieldset className="mt-3 border-0 m-0 p-0 min-w-0">
              <legend className="sr-only">
                {t("dashboard.features.lmc.autoPilot.sendWindowDays")}
              </legend>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {WEEK_DAYS.map((day) => {
                  const inputId = `auto-pilot-day-${day}`;
                  return (
                    <label
                      key={day}
                      htmlFor={inputId}
                      className="flex cursor-pointer items-center gap-2 text-sm text-gray-900"
                    >
                      <input
                        id={inputId}
                        type="checkbox"
                        checked={sendWindow.days.includes(day)}
                        onChange={(e) => toggleSendDay(day, e.target.checked)}
                        className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="capitalize">
                        {moment().isoWeekday(day).format("ddd")}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
            {windowProblem && (
              <p className="mt-2 text-xs text-red-600" role="alert">
                {t(`dashboard.features.lmc.autoPilot.${windowProblem}`)}
              </p>
            )}
          </div>

          <div className="border-t border-gray-200 pt-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
              <div>
                <FieldLabel className="mb-0">
                  {t("dashboard.features.lmc.autoPilot.allowPromotionTitle")}
                </FieldLabel>
                <p className="mb-1 text-xs text-gray-500">
                  {t("dashboard.features.lmc.autoPilot.allowPromotionHelp")}
                </p>
              </div>
              <div className="self-start sm:self-auto">
                <Switch
                  checked={autoPilotConfig.allow_promotion}
                  disabled={!canWrite}
                  onChange={handleToggleAllowPromotion}
                />
              </div>
            </div>

            {autoPilotConfig.allow_promotion && (
              <div className="mt-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                  <div>
                    <p className="mb-1 text-xs text-gray-500">
                      {t(
                        "dashboard.features.lmc.autoPilot.promotionMessagesHelp"
                      )}
                    </p>
                  </div>
                  {autoPilotConfig.promotion_messages.length <
                    AUTO_PILOT_LIMITS.offersMax && (
                    <CustomButton
                      type="button"
                      variant="none"
                      size="sm"
                      onClick={handleAddPromotionMessage}
                      className="shrink-0 self-start gap-1 whitespace-nowrap px-0 text-blue-600 enabled:hover:text-blue-700"
                      icon={<HiPlus size={16} />}
                      title={t("dashboard.features.lmc.autoPilot.addPromotion")}
                    />
                  )}
                </div>
                <div className="space-y-3">
                  {autoPilotConfig.promotion_messages.map((message, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <div className="flex-1">
                        <TextInput
                          value={message}
                          onChange={(e) =>
                            handlePromotionMessageChange(index, e.target.value)
                          }
                          placeholder={
                            t(
                              "dashboard.features.lmc.autoPilot.promotionMessagePlaceholder"
                            ) as string
                          }
                          maxLength={AUTO_PILOT_LIMITS.offerMaxLength}
                          className="w-full"
                        />
                        <CharacterCounter
                          currentLength={message.length}
                          maxLength={AUTO_PILOT_LIMITS.offerMaxLength}
                        />
                      </div>
                      <CustomButton
                        type="button"
                        variant="none"
                        size="icon"
                        onClick={() => handleRemovePromotionMessage(index)}
                        className="mt-1 rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                        icon={<HiOutlineTrash size={18} />}
                        aria-label={t("common.delete") as string}
                      />
                    </div>
                  ))}
                </div>
                <p
                  className={`text-xs mt-2 ${
                    filled(autoPilotConfig.promotion_messages).length <
                    AUTO_PILOT_LIMITS.offersMin
                      ? "text-red-500"
                      : "text-gray-500"
                  }`}
                >
                  {t(
                    "dashboard.features.lmc.autoPilot.promotionMessagesCount",
                    {
                      count: filled(autoPilotConfig.promotion_messages).length,
                      min: AUTO_PILOT_LIMITS.offersMin,
                      max: AUTO_PILOT_LIMITS.offersMax,
                    }
                  )}
                </p>
              </div>
            )}
          </div>

          <div
            className="flex justify-end pt-4 border-t border-gray-200"
            translate="no"
          >
            <CustomButton
              onClick={handleSaveConfiguration}
              disabled={isSubmitting}
              title={
                isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <Spinner size="sm" />
                    {t("dashboard.features.lmc.autoPilot.saving")}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <HiSave size={18} />
                    {t("dashboard.features.lmc.autoPilot.saveConfig")}
                  </span>
                )
              }
              variant="primary"
            />
          </div>
        </fieldset>
      )}
    </div>
  );
};

export default AutoPilotSettings;
